import { createContext, useContext, useState, useRef, useEffect } from 'react';

const MusicContext = createContext();

export const useMusic = () => useContext(MusicContext);

export const MusicProvider = ({ children }) => {
    const [activeTrack, setActiveTrack] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSeeking, setIsSeeking] = useState(false);
    const [targetTab, setTargetTab] = useState(null);
    const audioRef = useRef(null);
    const animationFrameRef = useRef(null);
    const lastUpdateTimeRef = useRef(0);

    const playTrack = (track) => {
        if (activeTrack?.id === track.id) {
            togglePlay();
        } else {
            setActiveTrack(track);
            setIsPlaying(true);
        }
    };

    const togglePlay = () => {
        setIsPlaying(prev => !prev);
    };
    
    const trackEnded = () => {
        setIsPlaying(false);
    };

    const closePlayer = () => {
        setActiveTrack(null);
        setIsPlaying(false);
    };

    const updateProgressSmooth = () => {
        if (!audioRef.current || isSeeking) {
            animationFrameRef.current = requestAnimationFrame(updateProgressSmooth);
            return;
        }

        const currentTime = audioRef.current.currentTime;
        const now = performance.now();

        if (now - lastUpdateTimeRef.current >= 16) {
            setProgress(currentTime);
            lastUpdateTimeRef.current = now;
        }

        if (isPlaying) {
            animationFrameRef.current = requestAnimationFrame(updateProgressSmooth);
        }
    };

    useEffect(() => {
        if (isPlaying && !isSeeking) {
            lastUpdateTimeRef.current = performance.now();
            animationFrameRef.current = requestAnimationFrame(updateProgressSmooth);
        } else {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        }

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [isPlaying, isSeeking, activeTrack]);
    
    useEffect(() => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.play().catch(e => console.error("Error playing audio:", e));
            } else {
                audioRef.current.pause();
            }
        }
    }, [isPlaying, activeTrack]);

    useEffect(() => {
        setProgress(0);
        setDuration(0);
    }, [activeTrack]);


    const value = {
        activeTrack,
        isPlaying,
        progress,
        duration,
        isSeeking,
        audioRef,
        targetTab,
        setTargetTab,
        playTrack,
        togglePlay,
        trackEnded,
        closePlayer,
        setDuration,
        setProgress,
        setIsSeeking,
    };

    return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
};
